import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, User, LogOut, Globe, Shield, Phone, RotateCcw, Star, Package, Award } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { pickTier } from "@/lib/loyalty";
import { TierBadge } from "@/components/TierBadge";

export const Header = () => {
  const { t, lang, toggle } = useLanguage();
  const { count, setOpen } = useCart();
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    api.get("/loyalty-config").then(({ data }) => setTiers(data.tiers || [])).catch(() => {});
  }, []);

  const userTier = user && !isAdmin ? pickTier(tiers, user.points_lifetime ?? user.points ?? 0) : null;

  const navItems = [
    { to: "/", label: t("home") },
    { to: "/rewards", label: t("rewardsNav") },
  ];

  return (
    <header className="glass sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-6">
        <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg border border-[#D4AF37] flex items-center justify-center font-display font-bold text-[#D4AF37] text-lg">
            M16
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace(/\//g, "-") || "home"}`}
              end={item.to === "/"}
              className={({ isActive }) =>
                `text-sm transition-colors hover:text-[#D4AF37] ${
                  isActive ? "text-[#D4AF37]" : "text-[#F3F4F6]/80"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-xs border border-[#27272A] rounded-full hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
          >
            <Globe size={14} />
            <span className="num-ltr">{lang === "ar" ? "EN" : "ع"}</span>
          </button>

          {user === null && (
            <Link
              to="/login"
              data-testid="header-login-link"
              className="hidden sm:flex items-center gap-2 text-sm text-[#F3F4F6]/80 hover:text-[#D4AF37] transition-colors"
            >
              <User size={16} />
              <span>{t("login")}</span>
            </Link>
          )}

          {user && (
            <div className="hidden sm:flex items-center gap-3">
              {isAdmin && (
                <Link to="/admin" data-testid="header-admin-link" className="flex items-center gap-1 text-xs text-[#D4AF37] hover:opacity-80">
                  <Shield size={14} /> {t("admin")}
                </Link>
              )}
              {!isAdmin && (
                <>
                  <Link to="/my-orders" data-testid="header-orders-link" className="flex items-center gap-1 text-xs text-[#F3F4F6]/80 hover:text-[#D4AF37] transition-colors">
                    <Package size={14} /> {t("myOrders")}
                  </Link>
                  {userTier && <TierBadge tier={userTier} size="sm" />}
                  <span className="flex items-center gap-1 text-xs text-[#D4AF37] num-ltr" data-testid="header-points" title={t("myPoints")}>
                    <Award size={14} /> {user.points || 0}
                  </span>
                </>
              )}
              <span className="text-xs text-[#9CA3AF]">{user.name || user.email}</span>
              <button
                onClick={async () => { await logout(); navigate("/login?logout=1"); }}
                data-testid="header-logout-btn"
                className="text-[#9CA3AF] hover:text-[#D4AF37]"
                title={t("logout")}
              >
                <LogOut size={16} />
              </button>
            </div>
          )}

          <button
            onClick={() => setOpen(true)}
            data-testid="header-cart-btn"
            className="relative p-2 rounded-full hover:bg-[#1C1C1F] transition-colors"
            aria-label={t("cart")}
          >
            <ShoppingBag size={20} className="text-[#F3F4F6]" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 rtl:-left-1 rtl:right-auto bg-[#D4AF37] text-black text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center num-ltr">
                {count}
              </span>
            )}
          </button>

          <button
            className="p-2"
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-[#27272A] px-6 py-4 space-y-3 bg-[#0A0A0B]">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              data-testid={`mobile-nav-${item.to.replace(/\//g, "-") || "home"}`}
              className="block text-sm py-1 text-[#F3F4F6]/80 hover:text-[#D4AF37]"
            >
              {item.label}
            </Link>
          ))}

          <div className="pt-3 border-t border-[#27272A] space-y-2">
            <Link to="/contact" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-contact" className="flex items-center gap-3 py-1.5 text-sm text-[#F3F4F6]/80 hover:text-[#D4AF37]">
              <Phone size={15} className="text-[#D4AF37]" /> {t("contactUs")}
            </Link>
            <Link to="/returns" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-returns" className="flex items-center gap-3 py-1.5 text-sm text-[#F3F4F6]/80 hover:text-[#D4AF37]">
              <RotateCcw size={15} className="text-[#D4AF37]" /> {t("returns")}
            </Link>
            <Link to="/reviews" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-reviews" className="flex items-center gap-3 py-1.5 text-sm text-[#F3F4F6]/80 hover:text-[#D4AF37]">
              <Star size={15} className="text-[#D4AF37]" /> {t("reviews")}
            </Link>
            {user && !isAdmin && (
              <Link to="/my-orders" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-orders" className="flex items-center justify-between py-1.5 text-sm text-[#F3F4F6]/80 hover:text-[#D4AF37]">
                <span className="flex items-center gap-3"><Package size={15} className="text-[#D4AF37]" /> {t("myOrders")}</span>
                <span className="inline-flex items-center gap-1 text-[#D4AF37] num-ltr"><Award size={13} /> {user.points || 0}</span>
              </Link>
            )}
            {user && isAdmin && (
              <Link to="/admin" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-admin" className="flex items-center gap-3 py-1.5 text-sm text-[#D4AF37]">
                <Shield size={15} /> {t("admin")}
              </Link>
            )}
          </div>

          <div className="flex items-center gap-3 pt-3 border-t border-[#27272A]">
            <button onClick={toggle} className="btn-outline-gold px-3 py-1.5 rounded-full text-xs" data-testid="mobile-lang-toggle">
              {lang === "ar" ? "EN" : "العربية"}
            </button>
            {user === null ? (
              <Link to="/login" onClick={() => setMobileOpen(false)} data-testid="mobile-login-link" className="text-sm text-[#D4AF37]">
                {t("login")}
              </Link>
            ) : (
              <button onClick={async () => { await logout(); setMobileOpen(false); navigate("/login?logout=1"); }} data-testid="mobile-logout-btn" className="text-sm text-[#D4AF37]">
                {t("logout")}
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
